"use strict";
/* eslint-disable no-bitwise */
Object.defineProperty(exports, "__esModule", { value: true });
exports.directionSplit = directionSplit;
exports.directionToName = directionToName;
exports.directionToInitial = directionToInitial;
exports.directionHasAll = directionHasAll;
exports.directionHasAny = directionHasAny;
exports.directionToSymbol = directionToSymbol;
exports.directionFromText = directionFromText;
exports.directionToOffset = directionToOffset;
exports.directionFromOffset = directionFromOffset;
exports.directionDiagonalToWestEast = directionDiagonalToWestEast;
exports.directionList = directionList;
const interfaces_1 = require("@lotr/interfaces");
/**
 * Splits a multi direction into an array of single directions
 *
 * @param direction An enum used to represent the directions
 * @returns An array with each direction
 * @example directionSplit(Direction.North | Direction.South) is [Direction.North, Direction.South]
 */
function directionSplit(direction) {
    const values = [];
    while (direction) {
        const bit = direction & (~direction + 1);
        values.push(bit);
        direction ^= bit;
    }
    return values;
}
/**
 * Converts the given single, or multi direction into its names, if the
 * direction is a multi direction, a seperator must be specified
 *
 * @param direction An enum used to represent the directions
 * @param seperator A string used to combine the symbol text
 * @returns A symbol, or symbols representing the direction.
 * ex: `Northeast` for Direction.Northeast, or `South North` for directionToName(Direction.North | Direction.South,' ')
 */
function directionToName(direction, seperator = null) {
    switch (direction) {
        case null:
            return 'Center';
        case interfaces_1.Direction.Center:
            return 'Center';
        case interfaces_1.Direction.Northwest:
            return 'Northwest';
        case interfaces_1.Direction.North:
            return 'North';
        case interfaces_1.Direction.Northeast:
            return 'Northeast';
        case interfaces_1.Direction.West:
            return 'West';
        case interfaces_1.Direction.East:
            return 'East';
        case interfaces_1.Direction.Southwest:
            return 'Southwest';
        case interfaces_1.Direction.South:
            return 'South';
        case interfaces_1.Direction.Southeast:
            return 'Southeast';
        default:
            if (!seperator) {
                throw new Error(`Failed to convert ${direction} to direction symbol`);
            }
            return directionSplit(direction)
                .map((dir) => directionToName(dir, null))
                .join(seperator);
    }
}
/**
 * Converts the given direction into its initials, if the
 * direction is a multi direction, a seperator must be specified
 *
 * @param direction An enum used to represent the directions
 * @param seperator A string used to combine the symbol text
 * @returns An initial, or initials representing the direction.
 * ex: `NE` for `directionToInitial(Direction.Northeast)`, or `S N` for `directionToInitial(Direction.North | Direction.South, ' ')`
 */
function directionToInitial(direction, seperator = null) {
    switch (direction) {
        case null:
            return 'C';
        case interfaces_1.Direction.Center:
            return 'C';
        case interfaces_1.Direction.Northwest:
            return 'NW';
        case interfaces_1.Direction.North:
            return 'N';
        case interfaces_1.Direction.Northeast:
            return 'NE';
        case interfaces_1.Direction.West:
            return 'W';
        case interfaces_1.Direction.East:
            return 'E';
        case interfaces_1.Direction.Southwest:
            return 'SW';
        case interfaces_1.Direction.South:
            return 'S';
        case interfaces_1.Direction.Southeast:
            return 'SE';
        default:
            if (!seperator) {
                throw new Error(`Failed to convert ${direction} to direction initial`);
            }
            return directionSplit(direction)
                .map((dir) => directionToInitial(dir, null))
                .join(seperator);
    }
}
/**
 * Checks if direction contains all mutli directions in contains
 *
 * @param direction The direction to check
 * @param contains The direction that must be in direction
 * @returns True if all directions in contains are in direction
 */
function directionHasAll(direction, contains) {
    return (direction & contains) === contains;
}
/**
 * Checks if direction contains any direcion in contains
 *
 * @param direction The direction to check
 * @param contains The direction that must overlap with direction
 * @returns True if any directions in contains are in direction
 */
function directionHasAny(direction, contains) {
    return (direction & contains) > 0;
}
/**
 * Converts the given direction into its symbols, if the
 * direction is a multi direction, a seperator must be specified
 *
 * @param direction An enum used to represent the directions
 * @param seperator A string used to combine the symbol text
 * @returns A symbol, or symbols representing the direction.
 * ex: `↗` for `directionToSymbol(Direction.Northeast)`, or `↓ ↑` for `directionToSymbol(Direction.North | Direction.South,' ')`
 */
function directionToSymbol(direction, seperator = null) {
    switch (direction) {
        case null:
            return '✧';
        case interfaces_1.Direction.Center:
            return '✧';
        case interfaces_1.Direction.Northwest:
            return '↖';
        case interfaces_1.Direction.North:
            return '↑';
        case interfaces_1.Direction.Northeast:
            return '↗';
        case interfaces_1.Direction.West:
            return '←';
        case interfaces_1.Direction.East:
            return '→';
        case interfaces_1.Direction.Southwest:
            return '↙';
        case interfaces_1.Direction.South:
            return '↓';
        case interfaces_1.Direction.Southeast:
            return '↘';
        default:
            if (seperator === null) {
                throw new Error(`Failed to convert ${direction} to direction symbol`);
            }
            return directionSplit(direction)
                .map((dir) => directionToSymbol(dir, null))
                .join(seperator);
    }
}
/**
 * Converts the given text into a single, or multi direction
 *
 * @param directionText A strin representing the direction.
 * Examples: `N`, `North`
 * @param seperator A string used to seperate the direction text.
 * @returns A direction, or combined direction, or null if it could not be parsed
 */
function directionFromText(directionText, seperator = null) {
    if (!directionText)
        return null;
    directionText = directionText.toUpperCase();
    switch (directionText) {
        case 'C':
            return interfaces_1.Direction.Center;
        case 'NW':
            return interfaces_1.Direction.Northwest;
        case 'N':
            return interfaces_1.Direction.North;
        case 'NE':
            return interfaces_1.Direction.Northeast;
        case 'W':
            return interfaces_1.Direction.West;
        case 'E':
            return interfaces_1.Direction.East;
        case 'SW':
            return interfaces_1.Direction.Southwest;
        case 'S':
            return interfaces_1.Direction.South;
        case 'SE':
            return interfaces_1.Direction.Southeast;
        case 'CENTER':
            return interfaces_1.Direction.Center;
        case 'NORTHWEST':
            return interfaces_1.Direction.Northwest;
        case 'NORTH':
            return interfaces_1.Direction.North;
        case 'NORTHEAST':
            return interfaces_1.Direction.Northeast;
        case 'WEST':
            return interfaces_1.Direction.West;
        case 'EAST':
            return interfaces_1.Direction.East;
        case 'SOUTHWEST':
            return interfaces_1.Direction.Southwest;
        case 'SOUTH':
            return interfaces_1.Direction.South;
        case 'SOUTHEAST':
            return interfaces_1.Direction.Southeast;
        case '✧':
            return interfaces_1.Direction.Center;
        case '↖':
            return interfaces_1.Direction.Northwest;
        case '↑':
            return interfaces_1.Direction.North;
        case '↗':
            return interfaces_1.Direction.Northeast;
        case '←':
            return interfaces_1.Direction.West;
        case '→':
            return interfaces_1.Direction.East;
        case '↙':
            return interfaces_1.Direction.Southwest;
        case '↓':
            return interfaces_1.Direction.South;
        case '↘':
            return interfaces_1.Direction.Southeast;
        default:
            if (!seperator)
                return null;
            return directionText
                .split(seperator)
                .map((dirT) => directionFromText(dirT, seperator) ?? interfaces_1.Direction.Center)
                .reduce((last, curr) => last | curr);
    }
}
/**
 * Converts a single direction to an offset.
 *
 * @param direction An enum to convert to an offset
 * @returns An offset. ex: Northwest is `{ x: -1, y: -1 }`
 */
function directionToOffset(direction) {
    switch (direction) {
        case null:
            return { x: 0, y: 0 };
        case interfaces_1.Direction.Center:
            return { x: 0, y: 0 };
        case interfaces_1.Direction.Northwest:
            return { x: -1, y: -1 };
        case interfaces_1.Direction.North:
            return { x: 0, y: -1 };
        case interfaces_1.Direction.Northeast:
            return { x: 1, y: -1 };
        case interfaces_1.Direction.West:
            return { x: -1, y: 0 };
        case interfaces_1.Direction.East:
            return { x: 1, y: 0 };
        case interfaces_1.Direction.Southwest:
            return { x: -1, y: 1 };
        case interfaces_1.Direction.South:
            return { x: 0, y: 1 };
        case interfaces_1.Direction.Southeast:
            return { x: 1, y: 1 };
        default:
            throw new Error(`Failed to convert ${direction} into a position`);
    }
}
/**
 * Converts an offset into a single direction
 *
 * @param offsetX The x offset from zero
 * @param offsetY The y offset from zero
 * @returns A direction
 */
function directionFromOffset(offsetX, offsetY) {
    if (offsetX > 0) {
        if (offsetY > 0)
            return interfaces_1.Direction.Southeast;
        if (offsetY < 0)
            return interfaces_1.Direction.Northeast;
        return interfaces_1.Direction.East;
    }
    if (offsetX < 0) {
        if (offsetY > 0)
            return interfaces_1.Direction.Southwest;
        if (offsetY < 0)
            return interfaces_1.Direction.Northwest;
        return interfaces_1.Direction.West;
    }
    if (offsetY > 0)
        return interfaces_1.Direction.South;
    if (offsetY < 0)
        return interfaces_1.Direction.North;
    return interfaces_1.Direction.Center;
}
/**
 * Replaces the single direction if it is diagonal, with west, or east depending on what is closer
 *
 * @param direction A single direction to convert to a cardinal single direction
 */
function directionDiagonalToWestEast(direction) {
    switch (direction) {
        case interfaces_1.Direction.Northwest:
            return interfaces_1.Direction.West;
        case interfaces_1.Direction.Northeast:
            return interfaces_1.Direction.East;
        case interfaces_1.Direction.Southwest:
            return interfaces_1.Direction.West;
        case interfaces_1.Direction.Southeast:
            return interfaces_1.Direction.East;
        default:
            return direction;
    }
}
/**
 * Returns an array of each direction, skips center
 */
function directionList() {
    return [
        interfaces_1.Direction.Northwest,
        interfaces_1.Direction.North,
        interfaces_1.Direction.Northeast,
        interfaces_1.Direction.West,
        interfaces_1.Direction.East,
        interfaces_1.Direction.Southwest,
        interfaces_1.Direction.South,
        interfaces_1.Direction.Southeast,
    ];
}
